import { Button, Card, Chip, faDigits, Field, formatDateShort, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type BomLineRow, type BomVersionRow, type StockItemRow } from "../../api.js";
import { tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";
import { formatMilli, formatQty, parseMilli } from "./format.js";

interface ComponentRow {
  id: string;
  name: string;
}

interface NewBomLineState {
  itemId: string;
  qtyInput: string;
  wastePercentInput: string;
}

export function BomTab() {
  const isManagerQuery = useQuery({
    queryKey: ["auth", "isManager"],
    queryFn: async () => {
      try {
        await api("/roles");
        return true;
      } catch {
        return false;
      }
    },
    staleTime: 60_000,
  });
  const isManager = Boolean(isManagerQuery.data);

  const components = useQuery({
    queryKey: ["menu", "components"],
    queryFn: () => api<ComponentRow[]>("/menu/lists/components").catch(() => []),
  });

  const items = useQuery({
    queryKey: ["inventory", "items"],
    queryFn: () => api<StockItemRow[]>("/inventory/items"),
  });

  const activeComponents = components.data ?? [];
  const activeItems = (items.data ?? []).filter((i) => i.status === "active");
  const itemsMap = new Map((items.data ?? []).map((i) => [i.id, i]));

  const [selectedCompId, setSelectedCompId] = useState<string>("");
  const currentCompId = selectedCompId || activeComponents[0]?.id || "";

  const boms = useQuery({
    queryKey: ["inventory", "boms", currentCompId],
    queryFn: () => api<BomVersionRow[]>(`/inventory/boms?componentId=${currentCompId}`),
    enabled: Boolean(currentCompId),
  });

  // New Version Form (Manager only)
  const [showAddVersion, setShowAddVersion] = useState(false);
  const [validFrom, setValidFrom] = useState(() => tehranToday());
  const [lines, setLines] = useState<NewBomLineState[]>([
    { itemId: "", qtyInput: "", wastePercentInput: "0" },
  ]);

  const addVersionAction = useAction(async () => {
    if (!currentCompId) return;
    const validLines: BomLineRow[] = [];
    for (const l of lines) {
      if (!l.itemId) continue;
      const qMilli = parseMilli(l.qtyInput);
      if (!qMilli || qMilli <= 0) throw new Error("مقدار هر پرس نامعتبر است.");
      const wastePct = Number(l.wastePercentInput || "0");
      const wasteFactorMilli = Math.round(wastePct * 10); // 5% -> 50 milli
      validLines.push({
        itemId: l.itemId,
        quantityPerPortionMilli: qMilli,
        wasteFactorMilli: Math.max(0, wasteFactorMilli),
      });
    }

    if (validLines.length === 0) {
      throw new Error("حداقل یک سطر ماده اولیه برای فرمول خام الزامی است.");
    }

    await api("/inventory/boms", {
      body: {
        componentId: currentCompId,
        validFrom,
        lines: validLines,
      },
    });

    setShowAddVersion(false);
    setValidFrom(tehranToday());
    setLines([{ itemId: "", qtyInput: "", wastePercentInput: "0" }]);
  }, [["inventory", "boms"]]);

  const addLine = () => {
    setLines([...lines, { itemId: "", qtyInput: "", wastePercentInput: "0" }]);
  };

  const removeLine = (idx: number) => {
    setLines(lines.filter((_, i) => i !== idx));
  };

  const updateLine = (idx: number, patch: Partial<NewBomLineState>) => {
    setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const selectedComp = activeComponents.find((c) => c.id === currentCompId);

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Component selector */}
      <div className="row wrap justify-between items-center" style={{ gap: "var(--s3)" }}>
        <div className="field" style={{ minWidth: "260px" }}>
          <label htmlFor="bom-comp">جزء غذا (کامپوننت منو)</label>
          <select
            id="bom-comp"
            className="input"
            value={currentCompId}
            onChange={(e) => {
              setSelectedCompId(e.target.value);
              setShowAddVersion(false);
            }}
          >
            {activeComponents.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Manager-only button: strictly hidden for non-managers */}
        {isManager && (
          <Button
            variant="default"
            icon="plus"
            onClick={() => {
              setShowAddVersion(!showAddVersion);
            }}
          >
            {showAddVersion ? "بستن فرم نسخه" : "ثبت نسخه جدید فرمول خام"}
          </Button>
        )}
      </div>

      {/* New BOM Form (Manager only) */}
      {isManager && showAddVersion && (
        <Card>
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <h3 className="t-card">ثبت نسخه جدید آنالیز ساخت (BOM) برای «{selectedComp?.name}»</h3>
            <div className="row wrap" style={{ gap: "var(--s3)" }}>
              <Field
                id="bom-valid-from"
                label="معتبر از تاریخ"
                type="date"
                value={validFrom}
                onChange={(e) => {
                  setValidFrom(e.target.value);
                }}
              />
            </div>

            <h4 className="t-card">اقلام و مقادیر ماده اولیه (برای ۱ پرس)</h4>
            <div className="stack" style={{ gap: "var(--s2)" }}>
              {lines.map((line, idx) => {
                const item = itemsMap.get(line.itemId);
                return (
                  <div
                    key={idx}
                    className="row wrap items-center"
                    style={{
                      gap: "var(--s2)",
                      padding: "var(--s2)",
                      backgroundColor: "var(--paper-subtle)",
                      borderRadius: "var(--radius-sm)",
                    }}
                  >
                    <div style={{ flex: "2 1 200px" }}>
                      <select
                        className="input"
                        value={line.itemId}
                        onChange={(e) => {
                          updateLine(idx, { itemId: e.target.value });
                        }}
                      >
                        <option value="">انتخاب ماده اولیه...</option>
                        {activeItems.map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.name} ({it.code}) - {it.baseUnit}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ flex: "1 1 140px" }}>
                      <input
                        className="input"
                        placeholder={`مقدار در هر پرس (${item?.baseUnit ?? "واحد"})`}
                        value={line.qtyInput}
                        onChange={(e) => {
                          updateLine(idx, { qtyInput: e.target.value });
                        }}
                      />
                    </div>

                    <div style={{ flex: "1 1 120px" }}>
                      <input
                        className="input"
                        placeholder="درصد ضایعات (مثلاً ۵)"
                        value={line.wastePercentInput}
                        onChange={(e) => {
                          updateLine(idx, { wastePercentInput: e.target.value });
                        }}
                      />
                    </div>

                    {lines.length > 1 && (
                      <Button
                        variant="ghost"
                        size="md"
                        onClick={() => {
                          removeLine(idx);
                        }}
                      >
                        حذف
                      </Button>
                    )}
                  </div>
                );
              })}

              <div>
                <Button variant="ghost" size="md" icon="plus" onClick={addLine}>
                  افزودن سطر ماده اولیه
                </Button>
              </div>
            </div>

            <ErrorLine error={addVersionAction.error} />

            <div className="row" style={{ gap: "var(--s2)" }}>
              <Button
                variant="primary"
                disabled={addVersionAction.busy}
                onClick={() => {
                  addVersionAction.run(undefined);
                }}
              >
                ذخیره نسخه فرمول
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowAddVersion(false);
                }}
              >
                انصراف
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Existing BOM Versions */}
      <Panel title={`نسخه‌های فرمول خام: ${selectedComp?.name ?? ""}`}>
        {!currentCompId ? (
          <p className="hint">لطفاً یک جزء منو را انتخاب کنید.</p>
        ) : boms.isLoading ? (
          <p className="hint">در حال بارگذاری فرمول‌های خام...</p>
        ) : (boms.data ?? []).length === 0 ? (
          <p className="hint">هنوز هیچ نسخه فرمول خامی برای این جزء تعریف نشده است.</p>
        ) : (
          <div className="stack" style={{ gap: "var(--s3)" }}>
            {(boms.data ?? []).map((ver) => (
              <Card key={ver.id}>
                <div className="stack" style={{ gap: "var(--s2)" }}>
                  <div className="row wrap justify-between items-center">
                    <div className="row items-center" style={{ gap: "var(--s2)" }}>
                      <Chip tone="blue">نسخه {faDigits(String(ver.version))}</Chip>
                      <span className="hint">شروع اعتبار از: {formatDateShort(ver.validFrom)}</span>
                    </div>
                  </div>

                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>ماده اولیه</th>
                          <th>کد کالا</th>
                          <th>مقدار در هر پرس</th>
                          <th>ضریب ضایعات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ver.lines.map((line, lIdx) => {
                          const item = itemsMap.get(line.itemId);
                          const wastePct = line.wasteFactorMilli / 10;
                          return (
                            <tr key={lIdx}>
                              <td>{item?.name ?? line.itemId}</td>
                              <td>{item?.code ?? "—"}</td>
                              <td className="num">
                                {formatQty(line.quantityPerPortionMilli, item?.baseUnit)}
                              </td>
                              <td className="num">
                                {wastePct > 0 ? (
                                  <Chip tone="warn" plain>
                                    ٪{faDigits(formatMilli(wastePct * 1000))}
                                  </Chip>
                                ) : (
                                  "بدون ضایعات"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
